import React from 'react';
import { Box, FormControl, FormLabel, Radio, RadioGroup } from '@mui/material';
import { Uniform } from '../../../../components/uniform/uniform';
import { UniformStepContainer } from '../../../../components/uniform/uniform-step-container';
import { UniformStep } from '../../../../components/uniform/uniform-step';
import { CourseTitlezone } from './course-titlezone';
import { CourseLaunchbox } from './course-launchbox';
import { Accordion, AccordionSummary, AccordionDetails } from '../../../../components/accordion';
import { Row } from '../../../../components/row';
import { Cell } from '../../../../components/cell';
import { MaxLengthTextField, TextField } from '../../../../components/text-field';
import { CompanyLookup, IUserLookup, TeamLookup, UserLookup } from '../../../../components/lookups';
import { OfferLookup } from '../../../../components/lookups/offer-lookup';
import { SingleSelect } from '../../../../components/single-select';
import { YesNo } from '../../../../components/radio-group/yes-no-component';
import { RadioOption } from '../../../../components/radio-group';
import { ReadonlyInput } from '../../../../components/readonly-input';
import { INumericValue, NumberField } from '../../../../components/number-fields';
import { ICompanyLookup } from '../../../../components/lookups/company-lookup/interfaces';
import { ClassroomDto, EducatorDto, OfferLookupDto, ReportType } from '../../../../api-service/clients';
import { ICourseState, ICourseStateErrors } from '../../interfaces';
import { LessonDataGrid } from '../components/lesson-table';
import { MultiSelect } from '../../../../components/multi-select';
import { ResponseTimeMarker } from '../../../../apm/response-time-measurement';

interface ICourseComponentProps {
  course: ICourseState;
  errors: ICourseStateErrors;
  isEditmode: boolean;
  isLoading: boolean;
  onCreateCourseClick: () => void;
  onUpdateCourseClick: () => void;
  onValidate: () => boolean;
  onYesNoChange: <K extends keyof ICourseState>(name: K, value: YesNo) => void;
  onStringChange: <K extends keyof ICourseState>(name: K, value?: string) => void;
  onNumberFieldChange: <K extends keyof ICourseState>(name: K, value?: number) => void;
  onEducatorsChange: (value?: EducatorDto[]) => void;
  onCompanyChange: (value?: ICompanyLookup) => void;
  onOfferChange: (value?: OfferLookupDto) => void;
  onFinishCourse: () => void;
  onDeleteCourse: () => void;
  isDeletingCourse: boolean;
  cannotBeDeletedReason?: string;
  onAddLesson: () => void;
  onOpenStatistics?: () => void;
  onRefreshLessons: () => void;
  classRooms: ClassroomDto[];
  educators: EducatorDto[];
  onPublishDraftLessonClicked: (lessonIds: string[]) => void;
  isSavingPublished: boolean;
  onCancelLessonsClicked: (lessonIds: string[], cancellationReason: string) => void;
  isSavingCancelled: boolean;
  onDeleteLessonsClicked: (lessonIds: string[]) => void;
  isDeletingLessons: boolean;
  onCaseWorkerChange: (caseWorker?: IUserLookup) => void;
  onTeamChange: (teamId: string) => void;
  canSeeOffer: boolean;
  hasAttendanceWrite: boolean;
}

export function CourseComponent(props: ICourseComponentProps) {
  const {
    isEditmode,
    onCreateCourseClick,
    onUpdateCourseClick,
    isLoading,
    onValidate,
    course,
    errors,
    onYesNoChange,
    onStringChange,
    onNumberFieldChange,
    onEducatorsChange,
    onCompanyChange,
    onOfferChange,
    onFinishCourse,
    onDeleteCourse,
    isDeletingCourse,
    cannotBeDeletedReason,
    onAddLesson,
    onOpenStatistics,
    onRefreshLessons,
    classRooms,
    educators,
    onPublishDraftLessonClicked,
    isSavingPublished,
    onCancelLessonsClicked,
    isSavingCancelled,
    onDeleteLessonsClicked,
    isDeletingLessons,
    onCaseWorkerChange,
    onTeamChange,
    canSeeOffer,
    hasAttendanceWrite,
  } = props;

  const {
    name,
    educatorIds,
    classroomId,
    responsibleCaseworkerId,
    responsibleTeamId,
    hasLimitedSeats,
    capacity,
    description,
    attendanceCheckStart,
    earliestArrivalOnTimeBeforeStart,
    latestArrivalOnTimeAfterStart,
    latestAttendanceRegistrationAfterStart,
    attendanceCheckEnd,
    earliestDepartureRegistrationBeforeEnd,
    earliestDepartureOnTimeBeforeEnd,
    latestDepartureOnTimeAfterEnd,

    createNegativeEventOnMissingAttendance,
    createFollowUpAdvisOnMissingAttendance,
    createAdvisIfNotOnTime,
    desiredReportType,
    companyId,
    offerId,
    isCompleted,
    lessons,
    hasDutyToWork,
    requiresAttendanceApproval,
  } = course;

  function handleYesNoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newValue = (event.target as HTMLInputElement).value as YesNo;
    const fieldName = (event.target as HTMLInputElement).name as keyof ICourseState;
    onYesNoChange(fieldName, newValue);
  }
  const isReadOnly = isCompleted || !hasAttendanceWrite;
  return (
    <>
      <ResponseTimeMarker measurement="FM_vishold" isReady={isLoading === false} />
      <Uniform
        titleZone={<CourseTitlezone isEditMode={isEditmode} courseName={name} isCompleted={isCompleted} />}
        launchbox={
          <CourseLaunchbox
            isLoading={isLoading}
            onSave={onUpdateCourseClick}
            onValidate={onValidate}
            isCompleted={isReadOnly}
            onFinishCourse={onFinishCourse}
            onAddLesson={onAddLesson}
            onOpenStatistics={onOpenStatistics}
            onDelete={onDeleteCourse}
            isDeleting={isDeletingCourse}
            cannotBeDeletedReason={cannotBeDeletedReason}
            hasAttendanceWrite={hasAttendanceWrite}
          />
        }
        isEditMode={isEditmode}
        showButtons={!isEditmode}
        onOk={onCreateCourseClick}
        onCancel={() => window.close()}
        okButtonIsLoading={isLoading}
        showSpinnerOnNextAndComplete={isLoading}
      >
        <UniformStep stepName="Generelt" validate={onValidate}>
          <UniformStepContainer>
            <Accordion defaultExpanded>
              <AccordionSummary>Om holdet</AccordionSummary>
              <AccordionDetails>
                <Box mt={1}>
                  <Row>
                    <Cell size="full">
                      {!isReadOnly ? (
                        <TextField
                          variant="outlined"
                          label="Titel"
                          multiline={false}
                          required
                          value={name}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onStringChange('name', event.target.value)}
                          lazy
                          error={!!errors.name}
                          errorText={errors.name}
                        />
                      ) : (
                        <ReadonlyInput value={name} label="Titel" />
                      )}
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="half">
                      <CompanyLookup value={companyId} required onChange={onCompanyChange} error={!!errors.companyId} errorText={errors.companyId} readOnly={isReadOnly} />
                    </Cell>
                    <Cell size="half">
                      <OfferLookup
                        placeholder={canSeeOffer ? 'Fremsøg tilbud' : 'Manglende rettigheder til at se tilbud'}
                        value={offerId}
                        onChange={onOfferChange}
                        readOnly={isReadOnly}
                        companyId={companyId}
                        disabled={!canSeeOffer}
                      />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="half">
                      {!isReadOnly ? (
                        <MultiSelect<EducatorDto>
                          label="Undervisere"
                          value={educators.filter(x => educatorIds?.includes(x.educatorId))}
                          options={educators}
                          getOptionLabel={x => x.name ?? ''}
                          renderOption={(prop: object, option: EducatorDto) => (
                            <li {...prop} key={option.educatorId}>
                              {option.name}
                            </li>
                          )}
                          onChange={(value?: EducatorDto[]) => onEducatorsChange(value)}
                          variant="outlined"
                        />
                      ) : (
                        <ReadonlyInput
                          value={educators
                            .filter(x => educatorIds?.includes(x.educatorId))
                            .map(x => x.name)
                            .join(', ')}
                          label="Underviser"
                        />
                      )}
                    </Cell>
                    <Cell size="half">
                      {!isReadOnly ? (
                        <SingleSelect<ClassroomDto>
                          label="Lokale"
                          placeholder="Fremsøg lokale"
                          value={classRooms.find(x => x.classroomId === classroomId)}
                          filterOptions={(options, state) => options.filter(x => x.isActive === true && x.name.toLowerCase().includes(state.inputValue.toLocaleLowerCase()))}
                          options={classRooms}
                          getOptionLabel={x => x.name}
                          renderOption={(prop: object, option: ClassroomDto) => (
                            <li {...prop} key={option.classroomId}>
                              {option.name}
                            </li>
                          )}
                          onChange={(value?: ClassroomDto) => onStringChange('classroomId', value?.classroomId)}
                          variant="outlined"
                        />
                      ) : (
                        <ReadonlyInput value={classRooms.find(x => x.classroomId === classroomId)?.name} label="Lokale" />
                      )}
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="half">
                      <UserLookup
                        label="Ansvarlig sagsbehandler"
                        placeholder="Fremsøg sagsbehandler"
                        value={responsibleCaseworkerId}
                        onChange={(_value: string, caseWorker?: IUserLookup) => onCaseWorkerChange(caseWorker)}
                        readOnly={isReadOnly}
                      />
                    </Cell>
                    <Cell size="half">
                      <TeamLookup
                        label="Ansvarligt team"
                        placeholder="Fremsøg team"
                        value={responsibleTeamId}
                        onChange={(value: string) => onTeamChange(value)}
                        readOnly={isReadOnly}
                      />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      {!isReadOnly ? (
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="active-radios" component="legend">
                            Begrænset antal pladser
                          </FormLabel>
                          <RadioGroup aria-labelledby="active-radios" row name="hasLimitedSeats" value={hasLimitedSeats ? YesNo.Yes : YesNo.No} onChange={handleYesNoChange}>
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <ReadonlyInput value={hasLimitedSeats ? 'Ja' : 'Nej'} label="Begrænset antal pladser" />
                      )}
                    </Cell>
                  </Row>
                  {hasLimitedSeats && (
                    <Row>
                      <Cell size="half">
                        {!isReadOnly ? (
                          <NumberField
                            variant="outlined"
                            label="Antal pladser"
                            value={capacity}
                            required={hasLimitedSeats}
                            onChange={(values: INumericValue) => onNumberFieldChange('capacity', values.floatValue)}
                            error={!!errors.capacity}
                            errorText={errors.capacity}
                          />
                        ) : (
                          <ReadonlyInput value={`${capacity}`} label="Antal pladser" />
                        )}
                      </Cell>
                    </Row>
                  )}
                  <Row>
                    <Cell size="full">
                      {!isReadOnly ? (
                        <MaxLengthTextField
                          rows={5}
                          maximizable
                          variant="outlined"
                          maxLength={16000}
                          label="Beskrivelse"
                          value={description}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onStringChange('description', event.target.value)}
                        />
                      ) : (
                        <ReadonlyInput value={description} label="Beskrivelse" />
                      )}
                    </Cell>
                  </Row>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary>Fremmødetjek</AccordionSummary>
              <AccordionDetails>
                <Box mt={1}>
                  <Row>
                    <Cell size="full">
                      {!isReadOnly ? (
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="active-radios" component="legend">
                            Fremmødetjek ved lektions start
                          </FormLabel>
                          <RadioGroup
                            aria-labelledby="active-radios"
                            row
                            name="attendanceCheckStart"
                            value={attendanceCheckStart ? YesNo.Yes : YesNo.No}
                            onChange={handleYesNoChange}
                          >
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <ReadonlyInput value={attendanceCheckStart ? 'Ja' : 'Nej'} label="Fremmødetjek ved lektions start" />
                      )}
                    </Cell>
                  </Row>
                  {attendanceCheckStart && (
                    <>
                      <Row>
                        <Cell size="half">
                          <ReadonlyInput label="Tidligste registrering af fremmøde (minutter før starttid)" value={`${earliestArrivalOnTimeBeforeStart || ''}`} />
                        </Cell>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <NumberField
                              variant="outlined"
                              label="Tidligst til tiden (minutter før starttid)"
                              required
                              value={earliestArrivalOnTimeBeforeStart}
                              onChange={(values: INumericValue) => onNumberFieldChange('earliestArrivalOnTimeBeforeStart', values.floatValue)}
                              error={!!errors.earliestArrivalOnTimeBeforeStart}
                              errorText={errors.earliestArrivalOnTimeBeforeStart}
                            />
                          ) : (
                            <ReadonlyInput value={`${earliestArrivalOnTimeBeforeStart}`} label="Tidligst til tiden (minutter før starttid)" />
                          )}
                        </Cell>
                      </Row>
                      <Row>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <NumberField
                              variant="outlined"
                              label="Senest til tiden: (minutter efter starttid)"
                              required
                              value={latestArrivalOnTimeAfterStart}
                              onChange={(values: INumericValue) => onNumberFieldChange('latestArrivalOnTimeAfterStart', values.floatValue)}
                              error={!!errors.latestArrivalOnTimeAfterStart}
                              errorText={errors.latestArrivalOnTimeAfterStart}
                            />
                          ) : (
                            <ReadonlyInput value={`${latestArrivalOnTimeAfterStart}`} label="Senest til tiden: (minutter efter starttid)" />
                          )}
                        </Cell>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <NumberField
                              variant="outlined"
                              label="Seneste registrering af fremmøde (minutter efter starttid)"
                              required
                              value={latestAttendanceRegistrationAfterStart}
                              onChange={(values: INumericValue) => onNumberFieldChange('latestAttendanceRegistrationAfterStart', values.floatValue)}
                              error={!!errors.latestAttendanceRegistrationAfterStart}
                              errorText={errors.latestAttendanceRegistrationAfterStart}
                            />
                          ) : (
                            <ReadonlyInput value={`${latestAttendanceRegistrationAfterStart}`} label="Seneste registrering af fremmøde (minutter efter starttid)" />
                          )}
                        </Cell>
                      </Row>
                    </>
                  )}
                  <Row>
                    <Cell size="full">
                      {!isReadOnly ? (
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="active-radios" component="legend">
                            Fremmødetjek ved lektions slutning
                          </FormLabel>
                          <RadioGroup aria-labelledby="active-radios" row name="attendanceCheckEnd" value={attendanceCheckEnd ? YesNo.Yes : YesNo.No} onChange={handleYesNoChange}>
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <ReadonlyInput value={attendanceCheckEnd ? 'Ja' : 'Nej'} label="Fremmødetjek ved lektions slutning" />
                      )}
                    </Cell>
                  </Row>
                  {attendanceCheckEnd && (
                    <>
                      <Row>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <NumberField
                              variant="outlined"
                              label="Tidligste registrering af afgang (minutter før sluttid)"
                              required
                              value={earliestDepartureRegistrationBeforeEnd}
                              onChange={(values: INumericValue) => onNumberFieldChange('earliestDepartureRegistrationBeforeEnd', values.floatValue)}
                              error={!!errors.earliestDepartureRegistrationBeforeEnd}
                              errorText={errors.earliestDepartureRegistrationBeforeEnd}
                            />
                          ) : (
                            <ReadonlyInput value={`${earliestDepartureRegistrationBeforeEnd}`} label="Tidligste registrering af afgang (minutter før sluttid)" />
                          )}
                        </Cell>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <NumberField
                              variant="outlined"
                              label="Tidligst til tiden (minutter før sluttid)"
                              required
                              value={earliestDepartureOnTimeBeforeEnd}
                              onChange={(values: INumericValue) => onNumberFieldChange('earliestDepartureOnTimeBeforeEnd', values.floatValue)}
                              error={!!errors.earliestDepartureOnTimeBeforeEnd}
                              errorText={errors.earliestDepartureOnTimeBeforeEnd}
                            />
                          ) : (
                            <ReadonlyInput value={`${earliestDepartureOnTimeBeforeEnd}`} label="Tidligst til tiden (minutter før sluttid)" />
                          )}
                        </Cell>
                      </Row>
                      <Row>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <NumberField
                              variant="outlined"
                              label="Senest til tiden: (minutter efter sluttid)"
                              required
                              value={latestDepartureOnTimeAfterEnd}
                              onChange={(values: INumericValue) => onNumberFieldChange('latestDepartureOnTimeAfterEnd', values.floatValue)}
                              error={!!errors.latestDepartureOnTimeAfterEnd}
                              errorText={errors.latestDepartureOnTimeAfterEnd}
                            />
                          ) : (
                            <ReadonlyInput value={`${latestDepartureOnTimeAfterEnd}`} label="Senest til tiden: (minutter efter sluttid)" />
                          )}
                        </Cell>
                        <Cell size="half">
                          <ReadonlyInput label="Seneste registrering af afgang (minutter efter sluttid)" value={`${latestDepartureOnTimeAfterEnd || ''}`} />
                        </Cell>
                      </Row>
                    </>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary>Forslag til opsætning af fremmødeforløb</AccordionSummary>
              <AccordionDetails>
                <Box mt={1}>
                  <Row>
                    <Cell size="full">
                      {!isReadOnly ? (
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="active-radios" component="legend">
                            Opret negativ hændelse på borger ved manglende fremmøde
                          </FormLabel>
                          <RadioGroup
                            aria-labelledby="active-radios"
                            row
                            name="createNegativeEventOnMissingAttendance"
                            value={createNegativeEventOnMissingAttendance ? YesNo.Yes : YesNo.No}
                            onChange={handleYesNoChange}
                          >
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <ReadonlyInput value={createNegativeEventOnMissingAttendance ? 'Ja' : 'Nej'} label="Opret negativ hændelse på borger ved manglende fremmøde" />
                      )}
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      {!isReadOnly ? (
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="active-radios" component="legend">
                            Opret advis på borger ved manglende fremmøde
                          </FormLabel>
                          <RadioGroup
                            aria-labelledby="active-radios"
                            row
                            name="createFollowUpAdvisOnMissingAttendance"
                            value={createFollowUpAdvisOnMissingAttendance ? YesNo.Yes : YesNo.No}
                            onChange={handleYesNoChange}
                          >
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <ReadonlyInput value={createFollowUpAdvisOnMissingAttendance ? 'Ja' : 'Nej'} label="Opret advis på borger ved manglende fremmøde" />
                      )}
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      {!isReadOnly ? (
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="active-radios" component="legend">
                            Opret advis på borger ved for sent fremmøde
                          </FormLabel>
                          <RadioGroup
                            aria-labelledby="active-radios"
                            row
                            name="createAdvisIfNotOnTime"
                            value={createAdvisIfNotOnTime ? YesNo.Yes : YesNo.No}
                            onChange={handleYesNoChange}
                          >
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <ReadonlyInput value={createAdvisIfNotOnTime ? 'Ja' : 'Nej'} label="Opret advis på borger ved for sent fremmøde" />
                      )}
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      {!isReadOnly ? (
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="active-radios" component="legend">
                            Indgår i arbejdspligt
                          </FormLabel>
                          <RadioGroup aria-labelledby="active-radios" row name="hasDutyToWork" value={hasDutyToWork ? YesNo.Yes : YesNo.No} onChange={handleYesNoChange}>
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <ReadonlyInput value={hasDutyToWork ? 'Ja' : 'Nej'} label="Indgår i arbejdspligt" />
                      )}
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      {!isReadOnly ? (
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="active-radios" component="legend">
                            Klarmeld protokol før godkendelse
                          </FormLabel>
                          <RadioGroup
                            aria-labelledby="active-radios"
                            row
                            name="requiresAttendanceApproval"
                            value={requiresAttendanceApproval ? YesNo.Yes : YesNo.No}
                            onChange={handleYesNoChange}
                          >
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <ReadonlyInput value={requiresAttendanceApproval ? 'Ja' : 'Nej'} label="Klarmeld protokol før godkendelse" />
                      )}
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="half">
                      {!isReadOnly ? (
                        <SingleSelect<ReportType>
                          label="Frekvens for fremmøderapporter"
                          placeholder="Sekvens"
                          value={desiredReportType}
                          options={[ReportType.Never, ReportType.Weekly, ReportType.Monthly]}
                          getOptionLabel={x => getReportDisplay(x) ?? ''}
                          renderOption={(prop: object, option: ReportType) => (
                            <li {...prop} key={option}>
                              {getReportDisplay(option)}
                            </li>
                          )}
                          onChange={(value: string) => onStringChange('desiredReportType', value)}
                          variant="outlined"
                          required
                          error={!!errors.desiredReportType}
                          errorText={errors.desiredReportType}
                        />
                      ) : (
                        <ReadonlyInput value={getReportDisplay(desiredReportType)} label="Frekvens for fremmøderapporter" />
                      )}
                    </Cell>
                  </Row>
                </Box>
              </AccordionDetails>
            </Accordion>
            {isEditmode && (
              <Accordion defaultExpanded>
                <AccordionSummary>Lektioner</AccordionSummary>
                <AccordionDetails>
                  <Box mt={1}>
                    <Row>
                      <Cell size="full">
                        <LessonDataGrid
                          onAddLesson={onAddLesson}
                          lessons={lessons}
                          onRefreshLessons={onRefreshLessons}
                          classRooms={classRooms}
                          isSavingPublished={isSavingPublished}
                          onPublishDraftLessonClicked={onPublishDraftLessonClicked}
                          onCancelLessonsClicked={onCancelLessonsClicked}
                          isSavingCancelled={isSavingCancelled}
                          onDeleteLessonsClicked={onDeleteLessonsClicked}
                          isDeletingLessons={isDeletingLessons}
                          isCourseCompleted={isReadOnly}
                          educators={educators}
                        />
                      </Cell>
                    </Row>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </UniformStepContainer>
        </UniformStep>
      </Uniform>
    </>
  );
}

function getReportDisplay(value: ReportType) {
  switch (value) {
    case ReportType.Never:
      return 'Aldrig';
    case ReportType.Weekly:
      return 'Ugenlig';
    case ReportType.Monthly:
      return 'Månedlig';
    default:
      return 'Aldrig';
  }
}
